import React, { useState, useEffect } from "react";
import ReportsOverview from "./ReportsOverview/ReportsOverview";
import EventsSummary from "./EventsSummary/EventsSummary";
import PaymentAnalytics from "./PaymentAnalytics/PaymentAnalytics";
import { getOrganiserReports } from "../../../revampCRUD/accountLevelReports.crud";
import {
  CURRENCY,
  dateFilterOptions,
  eventStatusOptions,
  getDateRangeISO,
  transformPaymentStatsData,
  viewTypeOptions,
} from "./AccountLevelReportConstant";
import moment from 'moment';
import PaymentGatewaysOverview from "./ReportsOverview/PaymentGatewaysOverview";
import customToast from "../../../components/common/basic/Toast";

const Reports = ({  selectedCurrency = null}) => {
  // State for all 3 dropdowns
  const [eventStatus, setEventStatus] = useState("active");
  const [dateFilter, setDateFilter] = useState("lastWeek");
  const [viewType, setViewType] = useState("card");

  //Filters for PaymentGatewaysOverview
  const [paymentGatewayFilters, setPaymentGatewayFilters] = useState({
    eventStatus: "past", 
    dateFilter: "lastWeek"
  });
  // Loading states for each section
  const [loading, setLoading] = useState({
    overview: false,
    events: false,
    payment: false,
    paymentGateways: false,
  });
  const [customDateRange, setCustomDateRange] = useState({
    startDate: null,
    endDate: null,
    isActive: false // Track if custom range is being used
  });

   // custom date range for payment gateways
  const [paymentGatewayCustomDateRange, setPaymentGatewayCustomDateRange] = useState({
    startDate: null,
    endDate: null,
    isActive: false
  });

  // Reports data state
  const [reportsData, setReportsData] = useState({
    eventsList: [],
    eventsCount: 0,
    paymentData: {
      gateways: [],
      methods: [],
      rawData: null
    },
  });
  // Add state to track if user manually changed view type
  const [isUserSelectedViewType, setIsUserSelectedViewType] = useState(false);
  const [hasInitialDataLoaded, setHasInitialDataLoaded] = useState(false);

  const OverviewDateFilterOptions = [...dateFilterOptions];

  const fetchEventsData = async (status = eventStatus, dateRange = dateFilter) => {
    try {
      setLoading(prev => ({ ...prev, events: true }));

      const customRange = dateRange === 'custom' && customDateRange.isActive ? customDateRange : null;
      const { from_timestamp, to_timestamp } = getDateRangeISO(dateRange, customRange);

      if (!from_timestamp || !to_timestamp) {
        return;
      }


      const response = await getOrganiserReports({
        metric: 'events_summary',
        from_timestamp,
        to_timestamp,
        event_status: status === 'all' ? undefined : status,
        event_currency: selectedCurrency?.currency_id
      });

      const apiData = response?.data || {};
      const eventsData = apiData.reports?.events_summary || [];
      const eventsCount = apiData.reports?.events_count || 0;

      setReportsData(prev => ({
        ...prev,
        eventsList: eventsData,
        eventsCount: eventsCount
      }));
      if (!hasInitialDataLoaded) {
        setHasInitialDataLoaded(true);
      }
    } catch (err) {
       customToast.error(err?.response?.data?.error || 'Something went wrong');
    } finally {
      setLoading(prev => ({ ...prev, events: false }));
    }
  };

  //  handler for card view pagination
  const handleCardPaginationChange = async (paginationParams) => {
    const { page, limit, offset, eventStatus, dateFilter } = paginationParams;

    try {
      setLoading(prev => ({ ...prev, events: true }));

      const customRange = dateFilter === 'custom' && customDateRange?.isActive ? customDateRange : null;
      const { from_timestamp, to_timestamp } = getDateRangeISO(dateFilter, customRange);

      const response = await getOrganiserReports({
        metric: 'events_summary',
        from_timestamp,
        to_timestamp,
        event_status: eventStatus === 'all' ? undefined : eventStatus,
        event_currency: selectedCurrency?.currency_id,
        limit: limit,
        offset: offset
      });

      const apiData = response?.data || {};
      const eventsData = apiData?.reports?.events_summary || [];
      const eventsCount = apiData?.reports?.events_count || 0;

      setReportsData(prev => ({
        ...prev,
        eventsList: eventsData,
        eventsCount: eventsCount
      }));

    } catch (err) {
      customToast.error(err?.response?.data?.error || 'Something went wrong');
    } finally {
      setLoading(prev => ({ ...prev, events: false }));
    }
  };
  const fetchPaymentData = async (dateRange) => {
    try {
      setLoading((prev) => ({ ...prev, payment: true }));

      const { from_timestamp, to_timestamp } = getDateRangeISO(dateRange);

      if (!from_timestamp || !to_timestamp) {
        return;
      }

      const response = await getOrganiserReports({
        metric: "payment_metrics",
        from_timestamp,
        to_timestamp,
        event_currency: selectedCurrency?.currency_id,
      });

      const apiData = response?.data || {};
      const transformedData = transformPaymentStatsData(apiData);
      setReportsData((prev) => ({
        ...prev,
         paymentData: {
          paymentMethods: transformedData.paymentMethods,
          paymentGateways: transformedData.paymentGateways,
        },
      }));
    } catch (err) {
      
       customToast.error(err?.response?.data?.error || 'Something went wrong');
         setReportsData((prev) => ({
        ...prev,
        paymentData: {
          paymentMethods: [],
          paymentGateways: [],
        },
      }));
    } finally {
      setLoading((prev) => ({ ...prev, payment: false }));
    }
  };


  const handleEventStatusChange = (option) => {
    setEventStatus(option?.value);
    setHasInitialDataLoaded(false);
    setIsUserSelectedViewType(false);

  };

  const handleDateFilterChange = (option) => {

    if (option?.value === 'custom') {
      if (option?.startDate && option?.endDate) {
        // custom dates r API call
        const newCustomRange = {
          startDate: option?.startDate,
          endDate: option?.endDate,
          isActive: true
        };

        setCustomDateRange(newCustomRange);
        setDateFilter('custom');
        setHasInitialDataLoaded(false);
        setIsUserSelectedViewType(false);
        fetchEventsDataWithCustomDates(eventStatus, newCustomRange);
      } else {
        customToast.error( 'Missing dates:', option);
      }
    } else {
      setCustomDateRange({ startDate: null, endDate: null, isActive: false });
      setDateFilter(option?.value);
      setHasInitialDataLoaded(false);
       setIsUserSelectedViewType(false);
      // fetchEventsData(eventStatus, option?.value);
    }
  };

  //  custom date fetching
  const fetchEventsDataWithCustomDates = async (status, customRange) => {
    try {
      setLoading(prev => ({ ...prev, events: true }));
      const from_timestamp = moment(customRange?.startDate)?.startOf('day')?.toISOString();
      const to_timestamp = moment(customRange?.endDate)?.endOf('day')?.toISOString();

      const response = await getOrganiserReports({
        metric: 'events_summary',
        from_timestamp,
        to_timestamp,
        event_status: status === 'all' ? undefined : status,
        event_currency: selectedCurrency?.currency_id
      });

      const apiData = response?.data || {};
      const eventsData = apiData.reports?.events_summary || [];
      const eventsCount = apiData.reports?.events_count || 0;

      setReportsData(prev => ({
        ...prev,
        eventsList: eventsData,
        eventsCount: eventsCount
      }));

      if (!hasInitialDataLoaded) {
        setHasInitialDataLoaded(true);
      }
    } catch (err) {
      customToast.error(err?.response?.data?.error || 'Something went wrong');
    } finally {
      setLoading(prev => ({ ...prev, events: false }));
    }
  };


  const handleViewTypeChange = async (option) => {
    const newViewType = option?.value;

    setViewType(newViewType);
    setIsUserSelectedViewType(true);

    try {
      setLoading((prev) => ({ ...prev, events: true }));

      const customRange = dateFilter === 'custom' && customDateRange.isActive ? customDateRange : null;
      const { from_timestamp, to_timestamp } = getDateRangeISO(dateFilter, customRange);

      if (!from_timestamp || !to_timestamp) {
        return;
      }

      let apiParams = {
        metric: "events_summary",
        from_timestamp,
        to_timestamp,
        event_status: eventStatus === "all" ? undefined : eventStatus,
        event_currency: selectedCurrency?.currency_id,
      };

      if (newViewType === "table") {
        apiParams.limit = 10;
        apiParams.offset = 0;
      } else if (newViewType === "card") {
        apiParams.limit = 6;  
        apiParams.offset = 0;
      }


      const response = await getOrganiserReports(apiParams);
      const apiData = response?.data || {};
      const eventsData = apiData?.reports?.events_summary || [];
      const eventsCount = apiData?.reports?.events_count || 0;

      setReportsData((prev) => ({
        ...prev,
        eventsList: eventsData,
        eventsCount: eventsCount,
      }));

    } catch (err) {
      customToast.error(err?.response?.data?.error || 'Something went wrong');
    } finally {
      setLoading((prev) => ({ ...prev, events: false }));
    }
  };


  // Handle pagination changes from EventTable
  const handlePaginationChange = async (paginationParams) => {
    const {
      limit,
      offset,
      search,
      eventStatus,
      dateFilter,
      isInitialTableLoad,
    } = paginationParams;

    try {
      setLoading((prev) => ({ ...prev, events: true }));

      const { from_timestamp, to_timestamp } = getDateRangeISO(dateFilter);

      const apiParams = {
        metric: "events_summary",
        from_timestamp,
        to_timestamp,
        event_status: eventStatus === "all" ? undefined : eventStatus,
        event_currency: selectedCurrency?.currency_id,
        limit: limit || 10,
        offset: offset || 0,
      };
      const response = await getOrganiserReports(apiParams);
      const apiData = response?.data || {};

      const eventsData = apiData?.reports?.events_summary || [];
      const eventsCount = apiData?.reports?.events_count || 0;

      setReportsData((prev) => ({
        ...prev,
        eventsList: eventsData,
        eventsCount: eventsCount,
      }));
    } catch (err) {
       customToast.error(err?.response?.data?.error || 'Something went wrong');
    } finally {
      setLoading((prev) => ({ ...prev, events: false }));
    }
  };

  const handlePaymentGatewayEventStatusChange = (option) => {
    const newFilters = {
      ...paymentGatewayFilters,
      eventStatus: option?.value
    };

    setPaymentGatewayFilters(newFilters);
    // fetchPaymentGatewayData(newFilters);
  };
  const fetchPaymentGatewayData = async (filters = paymentGatewayFilters,eventStatus= "active",customDateRange = null) => {
    try {
      setLoading((prev) => ({ ...prev, paymentGateways: true }));

      const customRange = filters?.dateFilter === 'custom' && customDateRange?.isActive
        ? customDateRange
        : null;

      const { from_timestamp, to_timestamp } = getDateRangeISO(filters?.dateFilter, customRange);

      if (!from_timestamp || !to_timestamp) {
        return;
      }
      const response = await getOrganiserReports({
        metric: "payment_metrics",
        from_timestamp,
        to_timestamp,
        event_currency: selectedCurrency?.currency_id,
        event_status: eventStatus === 'all' ? undefined : eventStatus,
      });

      const apiData = response?.data || {};
      const transformedData = transformPaymentStatsData(apiData);

      setReportsData((prev) => ({
        ...prev,
        paymentGatewayData: {
          gateways: transformedData?.paymentGateways || [],
          methods: transformedData?.paymentMethods || [],
        },
      }));

    } catch (err) {
      customToast.error(err?.response?.data?.error || 'Something went wrong');
      setReportsData((prev) => ({
        ...prev,
        paymentGatewayData: {
          gateways: [],
          methods: [],
        },
      }));
    } finally {
      setLoading((prev) => ({ ...prev, paymentGateways: false }));
    }
  };
  const handlePaymentGatewayDateFilterChange = (option) => {
    if (option?.value === 'custom') {
      if (option?.startDate && option?.endDate) {
        const newCustomRange = {
          startDate: option?.startDate,
          endDate: option?.endDate,
          isActive: true
        };
        setPaymentGatewayCustomDateRange(newCustomRange);

        const newFilters = {
          ...paymentGatewayFilters,
          dateFilter: 'custom'
        };

        setPaymentGatewayFilters(newFilters);
        // fetchPaymentGatewayData(newFilters);
      } else {
        customToast.error('Missing dates:', option);
      }
    } else {
      setPaymentGatewayCustomDateRange({ startDate: null, endDate: null, isActive: false });

      const newFilters = {
        ...paymentGatewayFilters,
        dateFilter: option?.value
      };

      setPaymentGatewayFilters(newFilters);
      fetchPaymentGatewayData(newFilters);
    }
  };

  useEffect(() => {
    if (dateFilter !== 'custom') {
      fetchEventsData(eventStatus, dateFilter);
    }
  }, [eventStatus, dateFilter, selectedCurrency]);

  useEffect(() => {
    if (customDateRange.isActive && dateFilter === 'custom') {
      fetchEventsData(eventStatus, 'custom');
    }
  }, [customDateRange, eventStatus, selectedCurrency]);

  useEffect(() => {
    if (selectedCurrency?.currency_id) {
      const isINR = selectedCurrency?.currency_name == 'INR';
      if (isINR) {
        const paymentFilters = {
          eventStatus: eventStatus,
          dateFilter: dateFilter
        };
        const customRange = dateFilter === 'custom' && customDateRange?.isActive ? customDateRange : null;
        fetchPaymentGatewayData(paymentFilters, eventStatus, customRange);
      } else {
        const customRange = paymentGatewayFilters?.dateFilter === 'custom' && paymentGatewayCustomDateRange?.isActive
          ? paymentGatewayCustomDateRange
          : null;
        fetchPaymentGatewayData(paymentGatewayFilters, paymentGatewayFilters.eventStatus, customRange);
      }

    }
  }, [selectedCurrency, paymentGatewayFilters, paymentGatewayCustomDateRange,eventStatus,dateFilter,customDateRange]);
  // Effect for payment data updates
  useEffect(() => {
    // fetchPaymentData(dateFilter);
  }, [dateFilter, selectedCurrency, customDateRange]);

  useEffect(() => {
    if (!isUserSelectedViewType && hasInitialDataLoaded && typeof reportsData?.eventsCount === 'number') {
      const shouldUseTableView = reportsData?.eventsCount > 20;
      const newViewType = shouldUseTableView ? "table" : "card";

      if (newViewType !== viewType) {
        setViewType(newViewType);
        handleViewTypeChange({ value: newViewType });
      }
    }
  }, [reportsData?.eventsCount, hasInitialDataLoaded, isUserSelectedViewType]);
  return (
    <div className="reports_main_container">

        <div className="w-100 richtext-w-full">
          <div className="row">
            <div className={`${selectedCurrency?.currency_name !== 'INR' ? 'col-4' : 'w-100 col-12'}`}>
              <ReportsOverview
                OverviewDateFilterOptions={OverviewDateFilterOptions}
                selectedCurrency={selectedCurrency}
              />
            </div>
            {selectedCurrency?.currency_name !== 'INR' && <div className="col-8">
                <PaymentGatewaysOverview
              selectedCurrency={selectedCurrency}
              dateFilterOptions={dateFilterOptions}
              eventStatusOptions={eventStatusOptions}
              eventStatus={paymentGatewayFilters?.eventStatus}
              dateFilter={paymentGatewayFilters?.dateFilter}
              paymentGatewayData={reportsData?.paymentGatewayData}
              loading={loading.paymentGateways}
              onDateFilterChange={handlePaymentGatewayDateFilterChange}
              onEventStatusChange={handlePaymentGatewayEventStatusChange}
              customDateRange={paymentGatewayCustomDateRange}
              onCustomDateChange={setPaymentGatewayCustomDateRange}
              />

            </div>}
          </div>


          <EventsSummary
            eventStatus={eventStatus}
            dateFilter={dateFilter}
            viewType={viewType}
            eventsList={reportsData.eventsList}
            eventsCount={reportsData.eventsCount}
            loading={loading.events}
            onEventStatusChange={handleEventStatusChange}
            onDateFilterChange={handleDateFilterChange}
            onViewTypeChange={handleViewTypeChange}
            eventStatusOptions={eventStatusOptions}
            dateFilterOptions={dateFilterOptions}
            viewTypeOptions={viewTypeOptions}
            onPaginationChange={handlePaginationChange}
            paymentStatsData={reportsData?.paymentGatewayData}
            paymentLoading={loading.payment}
            selectedCurrency={selectedCurrency}
            onCardPaginationChange={handleCardPaginationChange}
          />

          {/* <PaymentAnalytics
            paymentData={reportsData.paymentData}
            dateFilter={dateFilter}
            loading={loading.payment}
            onDateFilterChange={handleDateFilterChange}
            dateFilterOptions={dateFilterOptions}
          /> */}
        </div>
      <style>{`
                .reports_main_container {
                    margin: 0 !important;
                    border-radius: 0px;
                    background-color: #fff;
                    padding: 30px;
                    overflow-y: auto;
                }
                
                .reports_main_container > div:not(:last-child) {
                    margin-bottom: 2rem;
                }
                
                @keyframes fadeIn {
                    from { opacity: 0.5; }
                    to { opacity: 1; }
                }
                
                .loading-section {
                    animation: fadeIn 0.3s ease-in-out;
                }
            `}</style>
    </div>
  );
};

export default Reports;
